'use client';

import { Dispatch, ReactNode, useCallback, useMemo, useReducer } from 'react';
import {
	Beacon,
	countSteps,
	FormContext,
	FormContext_Actions,
	FormContext_Value,
	Reducer,
	UpdateActiveStep,
} from '../FormContext';

export interface FormProviderProps<Steps extends string, State, Action> {
	testId?: string;
	children: ReactNode;
	onSubmit: (state: State, dispatch: Dispatch<Action>) => void;
	reducer: Reducer<State, Action>;
	defaultState: State;
	initializeState?: () => State;
	graph: Beacon<Steps>;
	className?: string;
}

const FormProvider = <Steps extends string, State, Action>(
	props: FormProviderProps<Steps, State, Action>,
) => {
	const {
		testId = 'FormProvider',
		children,
		onSubmit,
		reducer,
		defaultState,
		initializeState = (state: State) => ({
			...state,
			graph,
			maxSteps: countSteps({ ...state, graph }),
		}),
		graph,
		className,
	} = props;
	const [state, dispatch] = useReducer(reducer, defaultState, initializeState);

	const registerStep: FormContext_Value<Steps, State, Action>['registerStep'] =
		useCallback(
			(name: Steps, onNext?: UpdateActiveStep, onBack?: UpdateActiveStep) => {
				dispatch({
					type: FormContext_Actions.REGISTER_STEP,
					payload: {
						name,
						onNext,
						onBack,
					},
				} as Action);
			},
			[],
		);

	const value: FormContext_Value<Steps, State, Action> = useMemo(
		() => ({
			submit: onSubmit,
			registerStep,
			state,
			dispatch,
			graph,
		}),
		[graph, onSubmit, registerStep, state],
	);

	return (
		<FormContext.Provider data-testid={testId} value={value}>
			<div className={className}>{children}</div>
		</FormContext.Provider>
	);
};

export default FormProvider;
